import {
    AppstoreOutlined,
    BarChartOutlined,
    CarOutlined,
    CarryOutOutlined,
    CreditCardOutlined,
    ExclamationCircleOutlined,
    InboxOutlined,
    LineChartOutlined,
    PullRequestOutlined,
    UserOutlined,
    ApartmentOutlined,
    EnvironmentOutlined,
    BlockOutlined,
    MonitorOutlined,
    ClockCircleOutlined,
    DownloadOutlined,
    TeamOutlined,
    HistoryOutlined,
    ScanOutlined,
    ScissorOutlined,
    SolutionOutlined,
    DashboardOutlined,
    SendOutlined
} from '@ant-design/icons';
import { ErrorInfo } from 'components/ErrorInfo';
import Loading from 'components/Loading';
import { NoPermission } from 'components/NoPermission';
import WarehouseSelector, { WarehouseDisplayMode, filterWarehousesForDisplayMode } from '../../../components/WarehouseSelector';
import React, { useCallback, useMemo, useLayoutEffect, useEffect, useState } from 'react';
import { useNavigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { isWarehouseManager, isFinancialTeam, isWarehouseOperator, isHubOperator, isSortingStaff, isDspManager, isDspOwnerTeam, isPickupDispatcher, isKAQC, isIncomeFinancialTeam, isCS, isCSFollowUp, isTmsDispatcher, isConfigAdmin, isRiskControl } from '../../../utils/auth';
import { getWmsWarehouseStorageKey } from '../../../utils/auth';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import { WMS_DAILY_TASK_MONITORING_PATH, WMS_DELIVERY_MONITOR_PATH, WMS_NO_UPDATE_WAREHOUSE_PATH } from '../routePaths';
import { isUnderTestHub } from '../../central/delivery-monitor/deliveryMonitorMotion';
import MainLayout from '../../../layouts/MainLayout';
import { useTranslation } from 'react-i18next';
import { WarehouseProvider ,useWarehouseContext} from '../../../contexts/WarehouseContext';
import { SiteType } from '../../../graphql/types/SiteType';
import { WarehouseBusinessType } from '../../../graphql/types/dashboard/SortingDashboardTypes';
import { useQuery } from '@apollo/client';
import { useGetAllAccessHubAndWarehousesForRole } from '../../../hooks/useWarehouseQuery';
import { useCurrentUserOperableHubAndWarehouses } from '../../../hooks/useCurrentUserOperableWarehouses';
import { useCheckWarehouseGrayConfig } from '../../../graphql/queries/checkWarehouseGrayConfig';
import {
    IS_MANUAL_PUSH_WAREHOUSE,
    IsManualPushWarehouseResponse,
    IsManualPushWarehouseVariables,
} from '../../../graphql/queries/isManualPushWarehouse';
import { useCheckPenaltyDspImportGray } from '../../../graphql/queries/checkPenaltyDspImportGray';
import { useIsCutoffVisible } from '../cutoff/graphql/queries/isCutoffVisible';import { useUnassignedDriverCount } from '../../../hooks/useUnassignedDriverCount';
import { renderHistoryMenuLabel } from 'components/parcelQueryRange/HistoryMenuTag';

// 辅助函数：找到第一个可用的菜单项（可能是直接的菜单项，或者是子菜单中的第一个）
const findFirstAvailableMenuItem = (menuItems: any[]): any | null => {
    for (const item of menuItems) {
        // 如果菜单项不可显示，跳过
        if (item.display === false) {
            continue;
        }

        // 如果有子菜单，找到第一个可用的子菜单项
        if (item.children && item.children.length > 0) {
            const firstChild = item.children.find((child: any) => child.display !== false);
            if (firstChild && firstChild.onClick) {
                return firstChild;
            }
        }

        // 如果菜单项本身有onClick，返回它
        if (item.onClick) {
            return item;
        }
    }
    return null;
};

// hubMenuItems 中各顶层菜单项使用的资源码，用于判断账号在 Hub 菜单组是否有任意可见入口
const HUB_MENU_RESOURCE_CODES = [
    ResourceCode.menu.HUB_DAILY_REVIEW,
    ResourceCode.menu.HUB_PROGRESS,
    ResourceCode.menu.HUB_DASHBOARD,
    ResourceCode.menu.HUB_BAGGING_TASK,
    ResourceCode.menu.HUB_SORTING_BATCH,
    ResourceCode.menu.HUB_BAG_LABEL,
    ResourceCode.menu.HUB_PACKAGE,
    ResourceCode.menu.HUB_LABEL_SEARCH,
    ResourceCode.menu.HUB_MASTER_BAG,
    ResourceCode.menu.HUB_SORTER,
    ResourceCode.menu.HUB_CUTOFF,
    ResourceCode.menu.HUB_EXCEPTION,
    ResourceCode.menu.HUB_TT_EXCEPTION_ORDER_QUERY,
    ResourceCode.menu.HUB_LABEL_REPRINT,
    ResourceCode.menu.HUB_DOWNLOAD,
    ResourceCode.menu.HUB_ACCOUNT,
    ResourceCode.menu.HUB_SITE_MANAGEMENT,
    ResourceCode.menu.HUB_GEOFENCE_BLACKLIST,
    ResourceCode.menu.HUB_DSP_BILLING,
    ResourceCode.menu.HUB_AB_ADDRESS_MANAGEMENT
];

// warehouseMenuItems 中各顶层菜单项使用的资源码，用于判断账号在 Warehouse 菜单组是否有任意可见入口
const WAREHOUSE_MENU_RESOURCE_CODES = [
    ResourceCode.menu.WHS_WORKBENCH,
    ResourceCode.menu.WHS_PROGRESS,
    ResourceCode.menu.WHS_DELIVERY_MONITOR,
    ResourceCode.menu.WHS_DASHBOARD,
    ResourceCode.menu.WHS_PACKAGE,
    ResourceCode.menu.WHS_LABEL_SEARCH,
    ResourceCode.menu.WHS_EXCEPTION,
    ResourceCode.menu.WHS_DELIVERY,
    ResourceCode.menu.WHS_DELIVERY_DRIVER,
    ResourceCode.menu.WHS_PENALTY_CENTER,
    ResourceCode.menu.WHS_DOWNLOAD,
    ResourceCode.menu.WHS_GEOFENCE_BLACKLIST,
    ResourceCode.menu.WHS_AB_ADDRESS_MANAGEMENT
];

const WmsLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const {t} = useTranslation();

    // 使用本地状态管理仓库数据
    const [currentWarehouse, setCurrentWarehouse] = useState<any>(null);
    const [isInitialized, setIsInitialized] = useState(false);


    const isExternalStaff= (): boolean => isHubOperator() || isSortingStaff();
    const externalStaff = isExternalStaff();

    const migrationQuery = useCurrentUserOperableHubAndWarehouses({
        skip: externalStaff,
        fetchPolicy: 'network-only'
    });
    const externalQuery = useGetAllAccessHubAndWarehousesForRole({
        skip: !externalStaff,
        fetchPolicy: 'cache-first'
    });
    const { warehouses: rawWarehouses, loading, error } = externalStaff ? externalQuery : migrationQuery;

    // 调试信息
    console.log('🏢 WmsLayout状态:', {
        rawWarehouses: rawWarehouses?.length,
        loading,
        error: error?.message,
        currentWarehouse: currentWarehouse?.id,
        currentPath: location.pathname,
        isInitialized
    });

    // 转换数据格式（未按展示模式过滤的原始列表，仅用于推导 warehouseDisplayMode）
    const allWarehouses = useMemo(() => {
        if (!rawWarehouses) return [];
        return rawWarehouses.map(warehouse => ({
            ...warehouse,
            id: warehouse.id,
            parentSiteId: warehouse.parentSiteId
        }));
    }, [rawWarehouses]);

    // 根据用户角色 + 可操作仓库类型 + 菜单资源码权限确定仓库选择器的展示模式
    // - 非内部员工 isHubOperator / isSortingStaff：保持原逻辑，HUB_ONLY
    // - 内部员工：某个类型只有同时满足"可操作仓库列表里有该类型数据"和"对应菜单组有资源码权限"才展示，
    //   避免选择器放出一个选中后二级菜单全空的类型
    //     · 两个类型都满足 → ALL
    //     · 只有 HUB 类型满足 → HUB_ONLY
    //     · 只有 WAREHOUSE 类型满足 → WAREHOUSE_ONLY
    //     · 都不满足（数据为空等过渡态）→ ALL
    const warehouseDisplayMode = useMemo(() => {
        if (isExternalStaff()) {
            return WarehouseDisplayMode.HUB_ONLY;
        }
        const hasHubData = allWarehouses.some((w: { siteType: number }) => w.siteType === SiteType.HUB);
        const hasWarehouseData = allWarehouses.some((w: { siteType: number }) => w.siteType === SiteType.WAREHOUSE);
        const hasHubMenuAccess = hasResource(HUB_MENU_RESOURCE_CODES);
        const hasWarehouseMenuAccess = hasResource(WAREHOUSE_MENU_RESOURCE_CODES);
        const showHub = hasHubData && hasHubMenuAccess;
        const showWarehouse = hasWarehouseData && hasWarehouseMenuAccess;
        if (showHub && showWarehouse) return WarehouseDisplayMode.ALL;
        if (showHub) return WarehouseDisplayMode.HUB_ONLY;
        if (showWarehouse) return WarehouseDisplayMode.WAREHOUSE_ONLY;
        return WarehouseDisplayMode.ALL;
    }, [allWarehouses]);

    // 按展示模式过滤后的仓库候选集合：下拉框展示的选项和这里的兜底选择必须用同一份规则算出来，
    // 避免"选中的仓库"和"下拉框里实际能选的仓库"不一致
    const warehouses = useMemo(
        // filterWarehousesForDisplayMode 只会从 allWarehouses 里筛掉部分元素，不会改变剩下元素的实际类型，
        // 断言回 allWarehouses 的类型以保持下游代码不变
        () => filterWarehousesForDisplayMode(allWarehouses, warehouseDisplayMode) as typeof allWarehouses,
        [allWarehouses, warehouseDisplayMode]
    );
    // 分批次-尾程：「批次推送」「批次推送历史」菜单只在当前仓为手动推送模式（准入配置推送模式=手动）时展示；
    // 只对 WHS 查询（HUB 没有推送模式），查询失败或未返回时按非手动处理、不展示
    const { data: manualPushData } = useQuery<IsManualPushWarehouseResponse, IsManualPushWarehouseVariables>(IS_MANUAL_PUSH_WAREHOUSE, {
        variables: { warehouseId: Number(currentWarehouse?.id) },
        skip: !currentWarehouse?.id || currentWarehouse.siteType !== SiteType.WAREHOUSE,
        fetchPolicy: 'cache-and-network',
    });
    const isManualPushWarehouse = manualPushData?.manual === true;
    // 问题件工作台入口按当前仓灰度：命中才展示（仅作用于「异常」子菜单下的问题件工作台入口）
    const { isInGrayConfig: isIssueWorkbenchGray } = useCheckWarehouseGrayConfig(
        'dsp.issue_parcel_workbench.gray_config',
        currentWarehouse?.id,
        !currentWarehouse,
    );
    //zipcode电子围栏灰度配置查询
    const { isInGrayConfig: isZipcodeMapGeofenceGray } = useCheckWarehouseGrayConfig(
        'zipcode.map.geofence.hub',
        currentWarehouse?.siteType === SiteType.HUB ? currentWarehouse?.id : currentWarehouse?.parentSiteId
    );
    console.log('zipcodeMapGeofenceGray', isZipcodeMapGeofenceGray)

    // 换单打印(order-reprint)灰度配置查询
    const { isInGrayConfig: isOrderReprintGray, loading: isOrderReprintGrayLoading } = useCheckWarehouseGrayConfig(
        'hub.order.reprint.gray',
        currentWarehouse?.id,
        !currentWarehouse || currentWarehouse.siteType !== SiteType.HUB
    );

    // 分拣批次(sorting-batch)菜单灰度配置查询：按仓控制批次菜单是否显示（对齐换单打印）
    const { isInGrayConfig: isSortingBatchGray } = useCheckWarehouseGrayConfig(
        'hub.sorting_batch.gray',
        currentWarehouse?.id,
        !currentWarehouse
    );

    // 罚单管理中心灰度：与罚单导入共用 partner_penalty.dsp_import_whitelist（key 在后端固定），当前仓关联的 DSP 任一命中才展示菜单
    const { isInGrayConfig: isPenaltyCenterGray, loading: isPenaltyCenterGrayLoading } = useCheckPenaltyDspImportGray({ warehouseId: currentWarehouse?.id });

    // 切换仓库后，若新仓不在罚单白名单且当前页还停在罚单管理中心，跳回 /wms 默认界面
    useEffect(() => {
        if (isPenaltyCenterGrayLoading) return;
        if (isPenaltyCenterGray) return;
        if (location.pathname.startsWith('/wms/penalty-center')) {
            navigate('/wms');
        }
    }, [isPenaltyCenterGray, isPenaltyCenterGrayLoading, location.pathname, navigate]);

    // Cutoff 页面：先按资源码鉴权，再按 hub+线路灰度（仅 HUB 站点且有资源码时查询；无资源码则跳过，避免后端 RequireResource 拒绝刷权限错误）
    const hasCutoffResource = hasResource([ResourceCode.menu.HUB_CUTOFF]);
    const { visible: isCutoffVisible } = useIsCutoffVisible(
        currentWarehouse?.id,
        !currentWarehouse || currentWarehouse.siteType !== SiteType.HUB || !hasCutoffResource
    );



    const unassignedCount = useUnassignedDriverCount(
        currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
        'wms',
    );

    // Hub菜单组
    const hubMenuItems = useMemo(() => [
        {
            key: 'hub-daily-review',
            label: t('hubReview.title'),
            icon: <DashboardOutlined />,
            display: hasResource([ResourceCode.menu.HUB_DAILY_REVIEW]),
            onClick: () => navigate('/wms/hub-daily-review')
        },
        {
            key: 'progress-submenu',
            label: t('wms.progress.title'),
            icon: <LineChartOutlined />,
            display: hasResource([ResourceCode.menu.HUB_PROGRESS]),
            children: [
                {
                    key: 'progress/work-progress',
                    label: t('wms.progress.menu.workProgress'),
                    icon: <LineChartOutlined />,
                    display: hasResource([ResourceCode.menu.HUB_PROGRESS_OPERATION]),
                    onClick: () => navigate('/wms/progress/work-progress')
                },
                {
                    key: 'progress/warehouse-status',
                    label: t('wms.progress.menu.warehouseStatus'),
                    icon: <LineChartOutlined />,
                    display: hasResource([ResourceCode.menu.HUB_PROGRESS_WAREHOUSE]),
                    onClick: () => navigate('/wms/progress/warehouse-status')
                },
                {
                    key: 'delivery/inhub-dashboard',
                    label: t('wms.delivery.inhubDashboard.menuTitle'),
                    icon: <BarChartOutlined />,
                    display: hasResource([ResourceCode.menu.HUB_DASHBOARD_WAREHOUSE]),
                    onClick: () => navigate('/wms/delivery/inhub-dashboard')
                },
                {
                    key: 'delivery/bag-stay-monitor',
                    label: t('wms.delivery.bagStayMonitor.menuTitle'),
                    icon: <BarChartOutlined />,
                    display: hasResource([ResourceCode.menu.HUB_BAG_STAY_MONITOR]),
                    onClick: () => navigate('/wms/delivery/bag-stay-monitor')
                },
                {
                    key: 'progress/hub-daily-task-monitoring',
                    label: t('wms.progress.menu.deliveryProgress'),
                    icon: <MonitorOutlined />,
                    display: hasResource([ResourceCode.menu.HUB_PROGRESS_DELIVERY]) && currentWarehouse?.businessType !== 1,
                    onClick: () => navigate('/wms/progress/hub-daily-task-monitoring')
                },
                {
                    key: 'progress/hub-parcel-sla-monitoring',
                    label: t('wms.delivery.parcelSlaMonitoring.menuTitle'),
                    icon: <ClockCircleOutlined />,
                    display: hasResource([ResourceCode.menu.HUB_PROGRESS_TRACKING]) && currentWarehouse?.businessType !== 1,
                    onClick: () => navigate('/wms/progress/hub-parcel-sla-monitoring')
                }
            ]
        },
        {
            key: 'delivery/dashboard',
            label: t('wms.dashboard.title'),
            icon: <LineChartOutlined />,
            display: (isDspManager() || isDspOwnerTeam()) || hasResource([ResourceCode.menu.HUB_DASHBOARD]),
            children: [
                {
                    key: 'delivery/dispatch-delivery-summary',
                    label: t('wms.dashboard.dspDeliveryManagement'),
                    display: (isDspManager() || isDspOwnerTeam()) || hasResource([ResourceCode.menu.HUB_DASHBOARD_DELIVERY]),
                    onClick: () => navigate('/wms/delivery/dispatch-delivery-summary')
                },
                {
                    key: 'no-update-hub',
                    label: t('wms.dashboard.noUpdateDetail'),
                    display: hasResource([ResourceCode.menu.HUB_DASHBOARD_STALE]),
                    onClick: () => navigate('/wms/no-update-hub')
                },
                {
                    key: 'delivery/delivery-completed-summary',
                    label: t('wms.delivery.deliveryCompletedSummary.shortMenuTitle'),
                    display: isDspManager() || isDspOwnerTeam(),
                    onClick: () => navigate('/wms/delivery/delivery-completed-summary')
                }
            ]
        },
        {
            key: 'consolidations',
            label: t('wms.baggingTask.title'),
            icon: <AppstoreOutlined />,
            display: hasResource([ResourceCode.menu.HUB_BAGGING_TASK]),
            onClick: () => navigate('/wms/consolidations')
        },
        {
            key: 'sorting-batch',
            label: t('wms.sortingBatch.title'),
            icon: <BlockOutlined />,
            // 仅尾程业务仓可用 + 按仓灰度（hub.sorting_batch.gray）控制在哪些仓显示
            display: isSortingBatchGray && hasResource([ResourceCode.menu.HUB_SORTING_BATCH]) && currentWarehouse?.businessType === WarehouseBusinessType.LAST_MILE,
            onClick: () => navigate('/wms/sorting-batch')
        },
        {
            key: 'bags',
            label: t('wms.bags.title'),
            icon: <InboxOutlined />,
            display: hasResource([ResourceCode.menu.HUB_BAG_LABEL]),
            onClick: () => navigate('/wms/bags')
        },
        {
            key: 'parcels-submenu',
            label: t('wms.parcels.title'),
            icon: <InboxOutlined />,
            display: hasResource([ResourceCode.menu.HUB_PACKAGE, ResourceCode.menu.HUB_LABEL_SEARCH]),
            children: [
                {
                    key: 'parcels',
                    label: t('wms.parcels.menu.list'),
                    display: hasResource([ResourceCode.menu.HUB_PACKAGE]),
                    onClick: () => navigate('/wms/parcels')
                },
                {
                    key: 'parcels/history',
                    label: renderHistoryMenuLabel(t('central.parcels.menu.history')),
                    display: hasResource([ResourceCode.menu.HUB_PACKAGE]),
                    onClick: () => navigate('/wms/parcels/history')
                },
                {
                    key: 'parcels/damaged-label-lookup',
                    label: t('damagedLabelLookup.menu'),
                    display: hasResource([ResourceCode.menu.HUB_LABEL_SEARCH]),
                    onClick: () => navigate('/wms/parcels/damaged-label-lookup')
                }
            ]
        },
        {
            key: 'client-cartons',
            label: t('wms.clientCartons.title'),
            icon: <InboxOutlined />,
            display: hasResource([ResourceCode.menu.HUB_MASTER_BAG]),
            onClick: () => navigate('/wms/client-cartons')
        },
        {
            key: 'sorter',
            label: t('wms.sorter.title'),
            icon: <InboxOutlined />,
            display: hasResource([ResourceCode.menu.HUB_SORTER]),
            children: [
                {
                    key: 'sorter',
                    label: t('wms.sorter.title'),
                    display: hasResource([ResourceCode.menu.HUB_SORTER_SORT]),
                    onClick: () => navigate('/wms/sorter')
                },
                {
                    key: 'sorterPlan',
                    label: t('wms.sorterPlan.title'),
                    display: hasResource([ResourceCode.menu.HUB_SORTER_PLAN]),
                    onClick: () => navigate('/wms/sorterPlan')
                },
                {
                    key: 'sorterSortingLog',
                    label: t('wms.sorterSortingLog.title'),
                    display: hasResource([ResourceCode.menu.HUB_SORTER_LOG]),
                    onClick: () => navigate('/wms/sorterSortingLog')
                }
            ]
        },

        {
            key: 'exceptions',
            label: t('wms.exceptions.title'),
            icon: <ExclamationCircleOutlined />,
            display: hasResource([ResourceCode.menu.HUB_EXCEPTION]),
            onClick: () => navigate('/wms/exceptions')
        },
        {
            key: 'tt-exception-orders',
            label: t('wms.ttExceptionOrders.title'),
            icon: <ExclamationCircleOutlined />,
            display: hasResource([ResourceCode.menu.HUB_TT_EXCEPTION_ORDER_QUERY]),
            onClick: () => navigate('/wms/tt-exception-orders')
        },
        {
            key: 'order-reprint',
            label: t('wms.orderReprint.menuTitle'),
            icon: <ScanOutlined />,
            display: isOrderReprintGray && (isExternalStaff() || hasResource([ResourceCode.menu.HUB_LABEL_REPRINT])),
            onClick: () => navigate('/wms/order-reprint')
        },
        {
            key: 'cutoff',
            label: t('wms.cutoff.title'),
            icon: <ScissorOutlined />,
            display: hasCutoffResource && isCutoffVisible,
            onClick: () => navigate('/wms/cutoff')
        },
        {
            key: 'download',
            label: t('wms.download.title'),
            icon: <DownloadOutlined />,
            display: hasResource([ResourceCode.menu.HUB_DOWNLOAD]),
            onClick: () => navigate('/wms/download')
        },
        {
            key: 'account',
            label: t('wms.account.title'),
            icon: <UserOutlined />,
            display: hasResource([ResourceCode.menu.HUB_ACCOUNT]),
            children: [
                {
                    key: 'account',
                    label: t('wms.account.title'),
                    display: hasResource([ResourceCode.menu.HUB_ACCOUNT_ACCOUNT]),
                    onClick: () => navigate('/wms/account')
                },
                {
                    key: 'account/sorting-labor-team',
                    label: t('wms.account.menu.sortingLaborTeam'),
                    display: hasResource([ResourceCode.menu.HUB_ACCOUNT_LABOR_TEAM]) &&
                        currentWarehouse?.siteType === SiteType.HUB,
                    onClick: () => navigate('/wms/account/sorting-labor-team')
                },
            ]
        },
        {
            key: 'sites',
            label: t('wms.sites.title'),
            icon: <ApartmentOutlined />,
            display: hasResource([ResourceCode.menu.HUB_SITE_MANAGEMENT]),
            onClick: () => navigate('/wms/sites')
        },
        {
            key: 'ab-address',
            label: t('wms.abAddress.menuTitle'),
            icon: <EnvironmentOutlined />,
            display: hasResource([ResourceCode.menu.HUB_AB_ADDRESS_MANAGEMENT]),
            onClick: () => navigate('/wms/ab-address')
        },
        {
            key: 'zipcode-map-geofence',
            label: t('wms.zipcodeMapGeofence.title'),
            icon: <BlockOutlined />,
            display: isZipcodeMapGeofenceGray && hasResource([ResourceCode.menu.HUB_GEOFENCE_BLACKLIST, ResourceCode.menu.WHS_GEOFENCE_BLACKLIST]),
            onClick: () => navigate('/wms/zipcode-map-geofence')
        },
        {
            key: 'dsp-bill',
            label: t('dspBill.title'),
            icon: <CreditCardOutlined />,
            display: hasResource([ResourceCode.menu.HUB_DSP_BILLING]),
            onClick: () => navigate('/wms/dsp-bill')
        }
    ], [navigate, t, currentWarehouse, isZipcodeMapGeofenceGray, isOrderReprintGray, isSortingBatchGray, isCutoffVisible, hasCutoffResource, unassignedCount]);

    // 派送监控：测试 HUB 下的仓不显示入口（大屏本身也不展示测试站点）；上级 HUB 从未按展示模式过滤的 allWarehouses 里找
    const showDeliveryMonitor = hasResource([ResourceCode.menu.WHS_DELIVERY_MONITOR])
        && !isUnderTestHub(currentWarehouse, allWarehouses);

    // Warehouse菜单组
    const warehouseMenuItems = useMemo(() => [
        {
            key: 'workbench',
            label: t('workbench.myWorkbench'),
            icon: <DashboardOutlined />,
            display: hasResource([ResourceCode.menu.WHS_WORKBENCH]),
            onClick: () => navigate('/wms/workbench')
        },
        {
            key: 'delivery-progress',
            label: t('wms.delivery.progress.title'),
            icon: <LineChartOutlined />,
            // 派送监控只挂 WHS_DELIVERY_MONITOR，只有它的账号也要能看到这个分组
            display: hasResource([ResourceCode.menu.WHS_PROGRESS]) || showDeliveryMonitor,
            children: [
                {
                    key: 'delivery/daily-task-monitoring',
                    label: t('wms.delivery.dailyTaskMonitoring.title'),
                    icon: <MonitorOutlined />,
                    display: hasResource([ResourceCode.menu.WHS_PROGRESS_DELIVERY]),
                    onClick: () => navigate(WMS_DAILY_TASK_MONITORING_PATH)
                },
                {
                    key: 'delivery/parcel-sla-monitoring',
                    label: t('wms.delivery.parcelSlaMonitoring.menuTitle'),
                    icon: <ClockCircleOutlined />,
                    display: hasResource([ResourceCode.menu.WHS_PROGRESS_TRACKING]),
                    onClick: () => navigate('/wms/delivery/parcel-sla-monitoring')
                },
                {
                    key: 'delivery/delivery-monitor',
                    label: t('central.deliveryMonitor.title'),
                    icon: <DashboardOutlined />,
                    display: showDeliveryMonitor,
                    onClick: () => navigate(WMS_DELIVERY_MONITOR_PATH)
                }
            ]
        },
        {
            key: 'delivery/dashboard',
            label: t('wms.dashboard.title'),
            icon: <LineChartOutlined />,
            display: (isDspManager() || isDspOwnerTeam()) || hasResource([ResourceCode.menu.WHS_DASHBOARD]),
            children: [
                {
                    key: 'delivery/dispatch-delivery-summary',
                    label: t('wms.dashboard.dspDeliveryManagement'),
                    display: (isDspManager() || isDspOwnerTeam()) || hasResource([ResourceCode.menu.WHS_DASHBOARD_DELIVERY]),
                    onClick: () => navigate('/wms/delivery/dispatch-delivery-summary')
                },
                {
                    key: 'no-update-warehouse',
                    label: t('wms.dashboard.noUpdateDetail'),
                    display: hasResource([ResourceCode.menu.WHS_DASHBOARD_STALE]),
                    onClick: () => navigate(WMS_NO_UPDATE_WAREHOUSE_PATH)
                },
                {
                    key: 'delivery/delivery-completed-summary',
                    label: t('wms.delivery.deliveryCompletedSummary.shortMenuTitle'),
                    display: isDspManager() || isDspOwnerTeam(),
                    onClick: () => navigate('/wms/delivery/delivery-completed-summary')
                }
            ]
        },
        {
            key: 'parcels-submenu',
            label: t('wms.parcels.title'),
            icon: <InboxOutlined />,
            display: hasResource([ResourceCode.menu.WHS_PACKAGE, ResourceCode.menu.WHS_LABEL_SEARCH]),
            children: [
                {
                    key: 'parcels',
                    label: t('wms.parcels.menu.list'),
                    display: hasResource([ResourceCode.menu.WHS_PACKAGE]),
                    onClick: () => navigate('/wms/parcels')
                },
                {
                    key: 'parcels/history',
                    label: renderHistoryMenuLabel(t('central.parcels.menu.history')),
                    display: hasResource([ResourceCode.menu.WHS_PACKAGE]),
                    onClick: () => navigate('/wms/parcels/history')
                },
                {
                    key: 'parcels/damaged-label-lookup',
                    label: t('damagedLabelLookup.menu'),
                    display: hasResource([ResourceCode.menu.WHS_LABEL_SEARCH]),
                    onClick: () => navigate('/wms/parcels/damaged-label-lookup')
                }
            ]
        },
        // 「异常」子菜单（异常包裹列表 + 库位管理）
        {
            key: 'exceptions-submenu',
            label: t('wms.exceptions.title'),
            icon: <ExclamationCircleOutlined />,
            display: hasResource([ResourceCode.menu.WHS_EXCEPTION]),
            children: [
                {
                    key: 'exceptionorder',
                    label: t('wms.exceptionorder.title'),
                    icon: <ExclamationCircleOutlined />,
                    display: hasResource([ResourceCode.menu.WHS_EXCEPTION_PACKAGE]),
                    onClick: () => navigate('/wms/exceptionorder')
                },
                {
                    key: 'location-management',
                    label: t('wms.locationManagement.title'),
                    icon: <PullRequestOutlined />,
                    display: hasResource([ResourceCode.menu.WHS_EXCEPTION_LOCATION]),
                    onClick: () => navigate('/wms/location-management')
                },
                {
                    key: 'issue-parcel-workbench',
                    label: t('delivery.menu.issueParcelWorkbench'),
                    icon: <ExclamationCircleOutlined />,
                    display: isIssueWorkbenchGray && hasResource([ResourceCode.menu.WHS_ISSUE_PARCEL_WORKBENCH]),
                    onClick: () => navigate('/wms/issue-parcel-workbench')
                }
            ]
        },
        {
            key: 'delivery-submenu',
            label: t('delivery.title'),
            icon: <CarOutlined />,
            display: hasResource([ResourceCode.menu.WHS_DELIVERY]),
            children: [
                {
                    key: 'batch-push',
                    label: t('wms.batchPush.title'),
                    icon: <SendOutlined />,
                    display: isManualPushWarehouse && hasResource([ResourceCode.menu.WHS_DELIVERY_BATCH_PUSH]),
                    onClick: () => navigate('/wms/batch-push')
                },
                {
                    key: 'batch-push-history',
                    label: t('wms.batchPushHistory.title'),
                    icon: <HistoryOutlined />,
                    display: isManualPushWarehouse && hasResource([ResourceCode.menu.WHS_DELIVERY_BATCH_PUSH_HISTORY]),
                    onClick: () => navigate('/wms/batch-push-history')
                },
                {
                    key: 'split-order',
                    label: <>{t('wms.routes.title')}{unassignedCount > 0 && <span style={{marginLeft: 4, fontSize: 12, lineHeight: '18px', minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 9, backgroundColor: '#ff4d4f', color: '#fff', padding: '0 5px'}}>{unassignedCount}</span>}</>,
                    icon: <ApartmentOutlined />,
                    display: hasResource([ResourceCode.menu.WHS_DELIVERY_ROUTE]),
                    onClick: () => navigate('/wms/split-order')
                },
                {
                    key: 'routes-new',
                    label: t('wms.routes.newTitle'),
                    icon: <ApartmentOutlined />,
                    display: false,
                    onClick: () => navigate('/wms/routes-new')
                },
                {
                    key: 'route-assign',
                    label: t('delivery.routeAssign.menuTitle'),
                    icon: <TeamOutlined />,
                    display: false,
                    onClick: () => navigate('/wms/route-assign')
                },
                {
                    key: 'routes-history',
                    label: t('wms.routes.historyTitle'),
                    icon: <HistoryOutlined />,
                    display: hasResource([ResourceCode.menu.WHS_DELIVERY_ROUTE_HISTORY]),
                    onClick: () => navigate('/wms/routes-history')
                },
                {
                    key: 'tasks',
                    label: t('delivery.menu.tasks'),
                    icon: <CarryOutOutlined />,
                    display: hasResource([ResourceCode.menu.WHS_DELIVERY_TASK]),
                    onClick: () => navigate('/wms/tasks')
                },
                {
                    key: 'review',
                    label: t('wms.review.title'),
                    icon: <PullRequestOutlined />,
                    display: hasResource([ResourceCode.menu.WHS_DELIVERY_DISPATCH]),
                    onClick: () => navigate('/wms/review')
                }
            ]
        },
        {
            key: 'delivery-driver',
            label: t('wms.deliveryDriver.title'),
            icon: <CarOutlined />,
            display: hasResource([ResourceCode.menu.WHS_DELIVERY_DRIVER]),
            onClick: () => navigate('/wms/delivery-driver')
        },
        {
            // 仓库只读查看本仓下所有 DSP 的罚单，与 /partner-control/penaltyCenter 复用同一页面组件
            key: 'penalty-center',
            label: t('partnerControl.menu.penaltyCenter'),
            icon: <SolutionOutlined />,
            display: isPenaltyCenterGray && hasResource([ResourceCode.menu.WHS_PENALTY_CENTER]),
            onClick: () => navigate('/wms/penalty-center')
        },
        {
            key: 'download',
            label: t('wms.download.title'),
            icon: <DownloadOutlined />,
            display: hasResource([ResourceCode.menu.WHS_DOWNLOAD]),
            onClick: () => navigate('/wms/download')
        },
        {
            key: 'ab-address',
            label: t('wms.abAddress.menuTitle'),
            icon: <EnvironmentOutlined />,
            display: hasResource([ResourceCode.menu.WHS_AB_ADDRESS_MANAGEMENT]),
            onClick: () => navigate('/wms/ab-address')
        },
        {
            key: 'zipcode-map-geofence',
            label: t('wms.zipcodeMapGeofence.title'),
            icon: <BlockOutlined />,
            display: isZipcodeMapGeofenceGray && hasResource([ResourceCode.menu.HUB_GEOFENCE_BLACKLIST, ResourceCode.menu.WHS_GEOFENCE_BLACKLIST]),
            onClick: () => navigate('/wms/zipcode-map-geofence')
        },
        {
            key: 'pod-exempt',
            label: t('podExempt.menu.wms'),
            icon: <EnvironmentOutlined />,
            // 纯资源码控制，与后端 @RequireResourceAndWarehouse(WHS_POD_EXEMPT_REVIEW) 同判据
            // （后端在资源码之外还会校验仓权限，HUB 账号自动含子仓）
            display: hasResource([ResourceCode.menu.WHS_POD_EXEMPT_REVIEW]),
            onClick: () => navigate('/wms/pod-exempt')
        }
    ], [
        navigate,
        t,
        isIssueWorkbenchGray,
        isZipcodeMapGeofenceGray,
        isPenaltyCenterGray,
        isManualPushWarehouse,
        unassignedCount,
        showDeliveryMonitor
    ]);

    // 页面刷新时，从URL参数或localStorage恢复仓库选择，或选择第一个可用仓库
    // 用 useLayoutEffect 而非 useEffect：确保 currentWarehouse 在浏览器绘制前算好，
    // 避免"接口已返回但仓库还未选定"这一帧被误渲染成 Loading/无权限
    useLayoutEffect(() => {
        if (warehouses.length === 0) {
            return; // 仓库数据还未加载，等待
        }

        // 优先从URL参数中获取warehouseId
        const urlWarehouseId = searchParams.get('warehouseId');
        const wmsWarehouseStorageKey = getWmsWarehouseStorageKey();
        const savedWarehouseId = wmsWarehouseStorageKey ? localStorage.getItem(wmsWarehouseStorageKey) : null;
        const isRootPath = location.pathname === '/wms' || location.pathname === '/wms/';
        
        // 如果已经初始化过，且 localStorage 有值，不需要重新初始化
        // 这样可以避免点击三级菜单时重新设置 currentMenu 导致闪动
        if (isInitialized && savedWarehouseId && !urlWarehouseId) {
            // 如果在根路径，说明是从其他一级菜单切换过来的（localStorage已被清空后又设置了）
            if (!isRootPath) {
                console.log('🏢 已初始化，跳过');
                return;
            }
        }

        console.log('🏢 初始化仓库选择...', { savedWarehouseId, pathname: location.pathname, isInitialized });
        
        let selectedWarehouse = null;
        const pathname = location.pathname;
        const isParcelSla = (pathname.includes('/delivery/parcel-sla-monitoring') || pathname.includes('parcel-sla-monitoring')) && !pathname.includes('hub-parcel');
        const isHubSla = pathname.includes('hub-parcel-sla-monitoring');
        // 兼容：普通页用 warehouseId；parcel-sla 用 warehouseIds（取第一个）；hub-sla 用 hubId
        let effectiveUrlWarehouseId: string | null = urlWarehouseId;
        if (isParcelSla || isHubSla) {
            const urlId = isParcelSla ? searchParams.get('warehouseIds') : searchParams.get('hubId');
            if (urlId) effectiveUrlWarehouseId = urlId.split(',')[0].trim() || null;
        }
        if (effectiveUrlWarehouseId) {
            selectedWarehouse = warehouses.find((w: { id: string }) => w.id === effectiveUrlWarehouseId) ?? null;
            if (selectedWarehouse && wmsWarehouseStorageKey) {
                localStorage.setItem(wmsWarehouseStorageKey, effectiveUrlWarehouseId);
            }
        }
        
        // 如果URL参数中没有，尝试从localStorage恢复
        if (!selectedWarehouse && savedWarehouseId) {
            selectedWarehouse = warehouses.find(w => w.id === savedWarehouseId);
        }
        
        // 如果没有找到保存的仓库，使用第一个可用的仓库
        if (!selectedWarehouse) {
            selectedWarehouse = warehouses[0];
            console.log('🏢 使用第一个可用仓库:', { 
                id: selectedWarehouse.id, 
                siteType: selectedWarehouse.siteType 
            });
        }
        
        // 设置选中的仓库
        setCurrentWarehouse(selectedWarehouse);
        
        // 标记初始化完成
        setIsInitialized(true);
        
        // 如果当前路径是 /wms 根路径，导航到第一个可用的菜单项
        if (isRootPath) {
            // isHubOperator / isSortingStaff 只能访问 order-reprint，需要灰度配置
            if (isExternalStaff()) {
                if (isOrderReprintGray) {
                    console.log('🏢 HubOperator，直接导航到 order-reprint');
                    navigate('/wms/order-reprint');
                }
                // 无灰度权限时不导航，渲染层会显示无权限提示
            } else {
                const selectedMenu = selectedWarehouse.siteType === SiteType.HUB ? hubMenuItems : warehouseMenuItems;
                const firstMenuItem = findFirstAvailableMenuItem(selectedMenu);
                if (firstMenuItem) {
                    console.log('🏢 根路径，导航到第一个菜单项:', firstMenuItem.key);
                    firstMenuItem.onClick();
                }
            }
        } else {
            // 保持当前路径，不进行导航，MainLayout会自动选中对应的菜单项
                console.log('🏢 保持当前路径:', location.pathname);
        }
    }, [warehouses, location.pathname, hubMenuItems, warehouseMenuItems, isInitialized, searchParams, isOrderReprintGray]);

    // URL 参数 -> 树节点：仅在 URL 或路径变化时应用（不依赖 currentWarehouse，避免点击树节点后 effect 用旧 URL 覆盖选中并反复请求）
    useEffect(() => {
        if (warehouses.length === 0) return;
        const pathname = location.pathname;
        const isParcelSla = (pathname.includes('/delivery/parcel-sla-monitoring') || pathname.includes('parcel-sla-monitoring')) && !pathname.includes('hub-parcel');
        const isHubSla = pathname.includes('hub-parcel-sla-monitoring');
        let urlWarehouseIdForSync: string | null = searchParams.get('warehouseId');
        if (isParcelSla) {
            const v = searchParams.get('warehouseIds');
            if (v) urlWarehouseIdForSync = v.split(',')[0].trim() || null;
        } else if (isHubSla) {
            urlWarehouseIdForSync = searchParams.get('hubId');
        }
        if (!urlWarehouseIdForSync) return;
        const warehouse = warehouses.find((w: { id: string }) => w.id === urlWarehouseIdForSync);
        if (!warehouse) return;
        setCurrentWarehouse((prev: { id: string } | null) => (prev?.id === warehouse.id ? prev : warehouse));
        const wmsWarehouseStorageKey = getWmsWarehouseStorageKey();
        if (wmsWarehouseStorageKey) {
            localStorage.setItem(wmsWarehouseStorageKey, warehouse.id);
        }
    }, [searchParams, location.pathname, warehouses]);

    // 仓库选择处理逻辑
    const handleWarehouseChange = useCallback((warehouseId: string, siteType: number) => {
        console.log('🏢 仓库选择触发:', { warehouseId, siteType });

        // 保存选择的仓库ID到localStorage
        const wmsWarehouseStorageKey = getWmsWarehouseStorageKey();
        if (wmsWarehouseStorageKey) {
            localStorage.setItem(wmsWarehouseStorageKey, warehouseId);
        }
        console.log('🏢 保存仓库ID到localStorage:', warehouseId);

        // 检查站点类型是否发生变化
        const previousSiteType = currentWarehouse?.siteType;
        const isSiteTypeChanged = !previousSiteType || previousSiteType !== siteType;

        // 找到选中的仓库
        const selectedWarehouse = warehouses.find(w => w.id === warehouseId);
        if (selectedWarehouse) {
            setCurrentWarehouse(selectedWarehouse);
        }

        // 只有站点类型发生变化时才重新导航
        if (isSiteTypeChanged) {
            const selectedMenu = siteType === SiteType.HUB ? hubMenuItems : warehouseMenuItems;
            // 导航到第一个可用的菜单项（可能是子菜单中的第一个）
            const firstMenuItem = findFirstAvailableMenuItem(selectedMenu);
            if (firstMenuItem) {
                console.log('🏢 站点类型变化，导航到第一个菜单项:', firstMenuItem.key);
                firstMenuItem.onClick();
            }
        } else {
            console.log('🏢 站点类型未变化，仅更新仓库选择，保持当前路径');
        }
    }, [warehouses, currentWarehouse, hubMenuItems, warehouseMenuItems]);

    // 页面内切站点后让树跟着选中（派送监控大屏切 HUB / WHS）：只改选中、不按站点类型跳页，页面留在原地；树里没有该站点时不动
    const selectWarehouseFromPage = useCallback((warehouseId: string) => {
        const selectedWarehouse = warehouses.find(w => w.id === warehouseId);
        if (!selectedWarehouse) return;
        const wmsWarehouseStorageKey = getWmsWarehouseStorageKey();
        if (wmsWarehouseStorageKey) {
            localStorage.setItem(wmsWarehouseStorageKey, warehouseId);
        }
        setCurrentWarehouse(selectedWarehouse);
    }, [warehouses]);

    // 根据当前仓库的站点类型决定显示的菜单 - 使用 useMemo 确保引用稳定
    const currentMenu = useMemo(() => {
        if (!currentWarehouse) return [];
        return currentWarehouse.siteType === SiteType.HUB ? hubMenuItems : warehouseMenuItems;
    }, [currentWarehouse?.siteType, hubMenuItems, warehouseMenuItems]);

    // currentMenu 一个可点击项都没有时（资源码不够、或灰度关闭导致唯一有权限的项也被隐藏），
    // 视为该仓库无权限，兜底展示 NoPermission 而不是空白的二级菜单
    const hasVisibleMenu = useMemo(
        () => findFirstAvailableMenuItem(currentMenu) !== null,
        [currentMenu]
    );

    // 仓库选择器组件 - 使用 useMemo 避免每次渲染都创建新的 JSX 元素
    const warehouseSelector = useMemo(() => (
        <div className="flex items-center gap-4 px-4 py-2">
            <WarehouseSelector
                onWarehouseChange={handleWarehouseChange}
                displayMode={warehouseDisplayMode}
                warehouses={warehouses}
                selectedWarehouseId={currentWarehouse?.id}
            />
        </div>
    ), [handleWarehouseChange, warehouseDisplayMode, warehouses, currentWarehouse?.id]);

    return (
        <WarehouseProvider currentWarehouse={currentWarehouse} changeWarehouse={selectWarehouseFromPage}>
            <MainLayout
                activeFirstMenu="wms"
                secondMenu={currentMenu}
                secondHeader={warehouseSelector}
            >
                <div className="p-6 h-full flex flex-col">
                    {/* 接口加载中显示 Loading；出错显示 ErrorInfo；无仓库权限显示 NoPermission；否则等 currentWarehouse 初始化完成后渲染子组件 */}
                    {loading || isOrderReprintGrayLoading ? <Loading/> : (
                        error ? <ErrorInfo error={error}/> : (
                            ((isExternalStaff()) && !isOrderReprintGray) || warehouses.length === 0 ||
                            (currentWarehouse && !hasVisibleMenu) ? (
                                <NoPermission/>
                            ) : (
                                currentWarehouse ? <Outlet/> : <Loading/>
                            )
                        )
                    )}
                </div>
            </MainLayout>
        </WarehouseProvider>
    );
};

export default WmsLayout;
